import React, { useRef, useState } from "react";
import {
  CheckCircle2,
  Clock,
  Download,
  FileVideo,
  Loader2,
  Upload,
  X,
} from "lucide-react";

export const MINT = "#10b981";
export const PURPLE = "#a855f7";

export const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Pending", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
    progress: { label: "In Progress", color: PURPLE, bg: "rgba(168,85,247,0.12)" },
    current: { label: "Current", color: MINT, bg: "rgba(16,185,129,0.12)" },
    approved: { label: "Approved", color: MINT, bg: "rgba(16,185,129,0.12)" },
    completed: { label: "Completed", color: MINT, bg: "rgba(16,185,129,0.12)" },
    rejected: { label: "Rejected", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
    revision: { label: "Revision", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
    submitted: { label: "Submitted", color: PURPLE, bg: "rgba(168,85,247,0.12)" },
    delivered: { label: "Delivered", color: PURPLE, bg: "rgba(168,85,247,0.12)" },
  };
  const value = map[status] || {
    label: status || "Unknown",
    color: "#94a3b8",
    bg: "rgba(148,163,184,0.12)",
  };

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider"
      style={{
        color: value.color,
        background: value.bg,
        borderColor: `${value.color}33`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: value.color, boxShadow: `0 0 10px ${value.color}` }}
      />
      {value.label}
    </span>
  );
};

export const GlassCard = ({ children, glow = "purple", className = "" }) => {
  const color = glow === "mint" ? MINT : PURPLE;

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6 ${className}`}
      style={{
        boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.05), 0 20px 60px -30px ${color}66`,
      }}
    >
      <div
        className="pointer-events-none absolute -right-20 -top-24 h-48 w-48 rounded-full opacity-30 blur-3xl"
        style={{ background: color }}
      />
      <div className="relative">{children}</div>
    </div>
  );
};

export const OrderTabs = ({ tabs, value, onChange }) => (
  <div className="rounded-2xl border border-white/10 bg-black/40 p-1 backdrop-blur-xl">
    <div className="flex gap-1">
      {tabs.map((tab) => {
        const active = value === tab.id;
        const Icon = tab.icon;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`relative flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition sm:text-sm ${
              active ? "text-white" : "text-white/50 hover:text-white/80"
            }`}
            style={
              active
                ? {
                    background: "rgba(255,255,255,0.06)",
                    boxShadow: `inset 0 0 0 1px rgba(16,185,129,0.25), 0 8px 24px -12px ${MINT}`,
                  }
                : undefined
            }
          >
            {Icon && <Icon size={14} style={{ color: active ? MINT : undefined }} />}
            {tab.label}
          </button>
        );
      })}
    </div>
  </div>
);

export const FieldRow = ({ label, value, mono = false }) => (
  <div className="flex flex-col gap-1 border-b border-white/5 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-white/40">
      {label}
    </span>
    <span className={`text-sm text-white/90 ${mono ? "font-mono" : ""} sm:text-right`}>
      {value}
    </span>
  </div>
);

export const MetricCard = ({ label, value, accent = "mint" }) => {
  const color = accent === "mint" ? MINT : PURPLE;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-4"
      style={{ boxShadow: `0 8px 30px -20px ${color}88` }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
        {label}
      </p>
      <p className="mt-1.5 text-2xl font-bold text-white">{value}</p>
      <div
        className="pointer-events-none absolute -bottom-8 -right-6 h-20 w-20 rounded-full opacity-20 blur-2xl"
        style={{ background: color }}
      />
    </div>
  );
};

export const PrimaryAction = ({ children, onClick, disabled, loading, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={`group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-black transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto ${className}`}
    style={{
      background: `linear-gradient(135deg, ${MINT}, #34d399)`,
      boxShadow: `0 16px 50px -12px ${MINT}, inset 0 0 0 1px rgba(255,255,255,0.1)`,
    }}
  >
    {loading && <Loader2 size={14} className="animate-spin" />}
    {children}
  </button>
);

export const GhostAction = ({ children, onClick, disabled, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-3.5 text-sm font-semibold uppercase tracking-wider text-white/70 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto ${className}`}
  >
    {children}
  </button>
);

export const FileCard = ({ file, onOpen }) => {
  const isRejected = Boolean(file?.rejected || file?.status === "rejected");
  const isProcessing = !file?.watermarkedUrl && (
    file?.processingPercentage !== undefined ||
    file?.status === "processing"
  );
  const progress = file?.processingPercentage ?? file?.progress ?? 0;
  const clickable = !isProcessing;
  const size =
    typeof file?.size === "number"
      ? `${(file.size / 1000000).toFixed(2)} MB`
      : file?.size || "-";

  return (
    <button
      type="button"
      onClick={clickable ? onOpen : undefined}
      disabled={!clickable}
      className={`group flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition ${
        isRejected
          ? "border-red-400/30 bg-red-500/10 hover:bg-red-500/15"
          : "border-white/10 bg-white/[0.04] hover:bg-white/[0.06]"
      } ${!clickable ? "cursor-wait opacity-80" : ""}`}
    >
      <span
        className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/5"
        style={{ color: isRejected ? "#fca5a5" : MINT }}
      >
        <FileVideo size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">{file?.name}</p>
        <p className="mt-0.5 truncate text-xs text-white/40">
          {size} {file?.type ? `• ${file.type}` : ""}
        </p>
        {isRejected && (
          <p className="mt-1 text-xs font-semibold text-red-300">Rejected</p>
        )}
        {isProcessing && (
          <div className="mt-2">
            <p className="mb-1 text-xs text-white/40">
              Processing watermark... {progress}%
            </p>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background: `linear-gradient(90deg, ${MINT}, ${PURPLE})`,
                }}
              />
            </div>
          </div>
        )}
      </div>
      {!isProcessing && !isRejected && (
        <Download
          size={16}
          className="shrink-0 text-white/30 transition group-hover:text-white/70"
        />
      )}
      {isRejected && <X size={16} className="shrink-0 text-red-300" />}
    </button>
  );
};

export const UploadDropzone = ({ fileRef, onFileChange, onPick, uploading, progress }) => {
  const internalRef = useRef(null);
  const inputRef = fileRef || internalRef;
  const [dragging, setDragging] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (onPick) onPick(file);
  };

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        handleFile(event.dataTransfer.files?.[0]);
      }}
      className={`group relative overflow-hidden rounded-3xl border border-dashed p-8 text-center transition ${
        dragging
          ? "border-emerald-400/60 bg-emerald-500/5"
          : "border-white/10 bg-white/[0.03] hover:border-emerald-400/40 hover:bg-white/[0.04]"
      }`}
    >
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex w-full flex-col items-center gap-3 disabled:cursor-not-allowed"
        aria-label="Upload final version"
      >
        <span
          className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/5"
          style={{ color: MINT, boxShadow: `0 0 30px -8px ${MINT}66` }}
        >
          {uploading ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} />}
        </span>
        <div>
          <p className="text-sm font-semibold text-white">
            {uploading ? `Uploading... ${progress || 0}%` : "Upload final version"}
          </p>
          <p className="mt-1 text-xs text-white/40">
            Drag and drop or click. MP4, MOV
          </p>
        </div>
        {uploading && (
          <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${progress || 0}%`,
                background: `linear-gradient(90deg, ${MINT}, #34d399)`,
              }}
            />
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".mp4, .mov"
        className="hidden"
        onChange={onFileChange}
      />
    </div>
  );
};

export const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse rounded-2xl bg-white/[0.04] ${className}`} />
);

export const ErrorCard = ({ message }) => (
  <div className="rounded-2xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-200">
    {message}
  </div>
);

export const SuccessCard = ({ message, icon: Icon = CheckCircle2 }) => (
  <div className="flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4">
    <Icon size={18} style={{ color: MINT }} className="mt-0.5 shrink-0" />
    <p className="text-sm text-emerald-100">{message}</p>
  </div>
);

export const InfoChip = ({ icon: Icon = Clock, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-white/60">
    <Icon size={11} />
    {children}
  </span>
);
